import React, { useState, useEffect, useRef } from 'react';
import { useCioAutocomplete } from '@constructor-io/constructorio-ui-autocomplete';
import './style.css';
import 'tailwindcss/tailwind.css';
import '../global.css';
import SearchIconSVG from "../icons/search_icon.svg";

interface SearchBarProps {
  userId?: string;
  priceZoneId?: string;
  cnstrcAPIKey: string;
  navigate: (url: string) => void;
}

const sanitizeInput = (input: string) => {
  return input?.replace(/[<>"'`;/]/g, '');
};

const HighlightMatchedTerms = ({ value, matched_terms }) => {
  let result = '';
  let matchedIndices = new Set();
  matched_terms.forEach(term => {
    let termIndex = value.indexOf(term);
    if (termIndex !== -1) {
      for (let i = termIndex; i < termIndex + term.length; i++) {
        matchedIndices.add(i);
      }
    }
  });
  for (let i = 0; i < value.length; i++) {
    if (matchedIndices.has(i)) {
      result += value[i];
    } else {
      result += `<b>${value[i]}</b>`;
    }
  }
  return <span dangerouslySetInnerHTML={{ __html: result }} />;
};

const setCookie = (name: string, value: any, days: number): void => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `; expires=${date.toUTCString()}`;
  document.cookie = `${name}=${JSON.stringify(value)}${expires}; path=/`;
};

const getCookie = (name: string): any[] => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0)
      return JSON.parse(c.substring(nameEQ.length, c.length));
  }
  return [];
};

const Autocomplete: React.FC<SearchBarProps> = ({ userId, priceZoneId, cnstrcAPIKey, navigate }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [showRecent, setShowRecent] = useState<boolean>(true);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const inputRef = useRef(null);


  useEffect(() => {
    const storedSearches = getCookie('recentSearches') || [];
    setRecentSearches(storedSearches);
    setIsMobileView(window.screen.width <= 768);
    const handleCloseSearch = () => {
      setIsFocused(false);
      setIsDropdownOpen(false);
      setShowRecent(false);
    };
    window.addEventListener('closeSearch', handleCloseSearch);
    return () => {
      window.removeEventListener('closeSearch', handleCloseSearch);
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setQuery('');
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleRecentSearchClick = (search: string): void => {
    inputRef.current?.blur();
    const sanitizedValue = sanitizeInput(search);
    if (!sanitizedValue) {
      return;
    }
    setIsDropdownOpen(false);
    setIsFocused(false);
    updateRecentSearches(sanitizedValue);
    setQuery(sanitizedValue);
    setShowRecent(false);
    setSearchQuery(sanitizedValue);
    navigate(`/cat?Ntt=${sanitizedValue}&Dy=1`);
  };

  const updateRecentSearches = (newSearch: string): void => {
    let updatedSearches = [...recentSearches];

    // Remove duplicate if exists
    updatedSearches = updatedSearches.filter((search) => search !== newSearch);

    updatedSearches.unshift(newSearch);

    if (updatedSearches.length > 5) {
      updatedSearches = updatedSearches.slice(0, 5);
    }
    setCookie('recentSearches', updatedSearches, 7);
    setRecentSearches(updatedSearches);
  };

  const handleSearch = (value: string, source: string): void => {
    const sanitizedValue = sanitizeInput(value);
    if (!sanitizedValue) {
      return;
    }
    setSearchQuery(sanitizedValue);
    if (sanitizedValue.length >= 3) {
      setShowRecent(false); // Hide recent searches when showing products
      if (source === "submit") {
        setIsDropdownOpen(false);
        updateRecentSearches(sanitizedValue);
      }
      else {
        setIsDropdownOpen(true);
      }
    } else {
      setIsDropdownOpen(true);
      setShowRecent(true); // Show recent searches when input length is less than 3
    }
  };

  const handleFocused = (): void => {
    const searchEvent = new CustomEvent('searchEvent', {
      detail: {
        showResults: true
      }
    });
    window.dispatchEvent(searchEvent);
    setIsFocused(true);
    setIsDropdownOpen(true);
    setShowRecent(inputProps.value.length < 3);
  };

  const SearchSuggestions = ({ suggestions }) => {
    return (
      <div className="sc-pb-3">
        <ul>
          {suggestions?.data?.map((suggestion) => (
            <li
              key={suggestion.data?.id}
              className="sc-cursor-pointer sc-flex sc-px-6 sc-py-3 sc-justify-between sc-items-center sc-text-sm sc-text-black sc-font-normal sc-border-b-[0.5px] sc-border-solid sc-border-[#D8D8D8] text-box-trim-both text-box-edge-cap"
              onClick={() => handleRecentSearchClick(suggestion?.value)}
            >
              <HighlightMatchedTerms
                value={suggestion?.value}
                matched_terms={suggestion?.matched_terms}
              />

            </li>
          ))}
        </ul>
      </div>
    );
  };

  const args = {
    apiKey: cnstrcAPIKey,
    onSubmit: (submitEvent: { query: string; originalQuery: string; }) => {
      const formattedQuery = sanitizeInput(submitEvent.query || submitEvent.originalQuery);
      if (!formattedQuery || formattedQuery.trim().length === 0) {
        return;
      }
      const searchEvent = new CustomEvent('searchEvent', {
        detail: {
          query: formattedQuery,
          showResults: false
        }
      });
      if (submitEvent.query) {
        window.dispatchEvent(searchEvent);
      }
      inputRef.current?.blur();
      setShowRecent(false);
      setQuery('');
      setIsFocused(false);
      handleSearch(formattedQuery, "submit");
    },
    onChange: (e: string) => { handleSearch(e, "change"); },
    onFocus: () => handleFocused(),
    cioJsClientOptions: {
      serviceUrl: 'https://wpkmgeuco-zone.cnstrc.com',
      ...(userId && { userId }),
    },
    advancedParameters: {
      fmtOptions: {
        fields: ['id', 'description', 'image_url', 'url', 'p10', 'p10_wp', 'p30', 'p30_wp', 'p60', 'p60_wp'],
      },
    },
    sections: [
      {
        indexSectionName: 'Products',
        numResults: 5,
      },
      {
        indexSectionName: 'Search Suggestions',
        numResults: 5,
      }
    ],
    placeholder: 'Search',
  };

  const {
    isOpen,
    sections,
    getFormProps,
    getLabelProps,
    getInputProps,
    getMenuProps,
    setQuery,
    autocompleteClassName,
  } = useCioAutocomplete(args);

  const searchSuggestions = sections.find(section => section.indexSectionName === "Search Suggestions");

  const inputProps = getInputProps();
  return (
    <div
      className={`sc-w-full ${isMobileView && isFocused
        ? 'sc-bg-white sc-z-[999] sc-h-screen'
        : 'sc-relative sc-h-full sc-z-[99]'
        } ${autocompleteClassName}`}
    >
      <form {...getFormProps()}>
        <div className={`sc-flex`}>
          <div
            className={`sc-flex sc-items-center sc-rounded-[1.5rem] sc-bg-[#F9F9F9] focus-within:sc-bg-white focus-within:sc-border focus-within:sc-border-solid focus-within:sc-border-[#DCDCDC]  sc-px-4 sc-py-3 sc-w-[379px] sc-h-[40px] sc-relative sc-z-10 ${isMobileView ? 'sc-m-4' : ''}`}
          >
            <div className="sc-flex sc-justify-center sc-items-center sc-p-1 sc-mr-2">
              <img src={SearchIconSVG} alt='' />
            </div>
            <div className="sc-flex-auto sc-text-12">
              <input id="cio-input" {...inputProps} ref={inputRef} />
            </div>
            <div className="sc-flex ">
              <button
                className="cio-clear-btn"
                data-testid="cio-clear-btn"
                hidden={!inputProps.value}
                onClick={() => {
                  setQuery('');
                  if (inputProps.id) {
                    setTimeout(
                      () => document.getElementById(inputProps.id)?.focus(),
                      100
                    );
                  }
                }}
                type="button"
                aria-label="Clear search field text"
              >
                <div className="cio-icon sc-w-6 sc-h-6 sc-p-1.5 sc-flex sc-items-center sc-justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M11.3327 0.666748L5.99935 6.00008M5.99935 6.00008L0.666016 11.3334M5.99935 6.00008L11.3327 11.3334M5.99935 6.00008L0.666016 0.666748"
                      stroke="black"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </button>
            </div>

          </div>
          {isMobileView && isFocused && (
            <div className='sc-flex sc-justify-center sc-items-center'>
              <button
                className="cio-clear-btn sc-text-black sc-text-center sc-font-wfuturamedium sc-text-12 sc-leading-none sc-uppercase text-box-trim-both text-box-edge-cap sc-mr-4"
                data-testid="cio-clear-btn"
                onClick={() => {
                  inputRef.current?.blur();
                  setQuery('');
                  setShowRecent(false);
                  setIsFocused(false);
                  setIsDropdownOpen(false);
                }}
                type="button"
                aria-label="Clear search field text"
              >
                CANCEL
              </button>
            </div>
          )}
        </div>
      </form>

      <div {...getMenuProps()}>
        {
          isDropdownOpen &&
          <div className="sc-flex">
            <div className={`sc-bg-white ${isMobileView ? 'sc-w-full' : 'sc-w-[379px]'
              } sc-rounded-[0.5rem] sc-mt-2 sc-relative sc-z-10`}>
              {showRecent && recentSearches.length > 0 && (
                <div className="sc-py-3">
                  <ul>
                    {recentSearches.map((search, index) => (
                      <li
                        key={index}
                        className="sc-cursor-pointer sc-flex sc-px-6 sc-py-3 sc-justify-between sc-items-center sc-text-sm sc-text-black sc-font-normal sc-border-b-[0.5px] sc-border-solid sc-border-[#D8D8D8]"
                        onClick={() => handleRecentSearchClick(search)}
                      >
                        {search}

                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className='sc-overflow-y-auto sm-range:sc-max-h-full sc-max-h-[586px]'>
                  {!showRecent && (
                    <>
                      <div className="sc-w-full sc-h-16 sc-py-4 sc-inline-flex sc-justify-center sc-items-start">
                        <div
                            onClick={() => handleRecentSearchClick(searchQuery)}
                            className="sc-px-7 sc-py-2 sc-flex sc-justify-center sc-items-center sc-cursor-pointer">
                          <div className="sc-justify-end sc-text-black sc-text-xs sc-font-medium sc-font-wfuturamedium sc-underline sc-uppercase sc-text-ellipsis sc-overflow-hidden">
                            See All "{searchQuery}" results
                          </div>
                        </div>
                      </div>
                      <SearchSuggestions suggestions={searchSuggestions} />
                    </>
                  )}
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  );
};

export default Autocomplete;
